import { Dropdown, Table } from 'antd';
import moment from 'moment';
import React from 'react';
import { DAYTIME_FORMAT } from '../../../constants';

const cellEllipsisStyle = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '100%',
  display: 'block',
};

const EllipsisCell = ({ value, children }) => {
  const text = value ?? children ?? '';
  return (
    <div title={text} style={cellEllipsisStyle}>
      {children ?? value ?? ''}
    </div>
  );
};

const isDeleted = (record) => {
  return (
    record.name &&
    record.name.startsWith('Deleted User ') &&
    record.email &&
    record.email.startsWith('deleted_') &&
    record.email.endsWith('@deleted.com')
  );
};

const StatusCell = ({ record, onStatusChange }) => {
  const deleted = isDeleted(record);
  let statusLabel, statusClass;

  if (deleted) {
    statusLabel = 'Deleted';
    statusClass = 'deleted';
  } else if (record.isActive) {
    statusLabel = 'Active';
    statusClass = 'active';
  } else {
    statusLabel = 'Inactive';
    statusClass = 'inactive';
  }

  if (deleted || !onStatusChange) {
    return <span className={`status users ${statusClass}`}>{statusLabel}</span>;
  }

  const menuItems = [
    record.isActive
      ? {
          key: 'inactive',
          label: <span className="status-dropdown-item inactive">Inactive</span>,
          onClick: () => onStatusChange(record, 'inactive'),
        }
      : {
          key: 'active',
          label: <span className="status-dropdown-item active">Active</span>,
          onClick: () => onStatusChange(record, 'active'),
        },
    { type: 'divider' },
    {
      key: 'delete',
      label: <span className="status-dropdown-item delete">Delete User</span>,
      onClick: () => onStatusChange(record, 'delete'),
    },
  ];

  return (
    <Dropdown menu={{ items: menuItems }} trigger={['hover']} placement="bottomLeft">
      <span className={`status users ${statusClass} clickable`}>
        <span>{statusLabel}</span>
      </span>
    </Dropdown>
  );
};

const UsersTable = ({ withAction, dataSource, selectedRowKeys, onSelectChange, onStatusChange, ...props }) => {
  const columns = [
    {
      title: 'User Name',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: 180,
      render: (value) => <EllipsisCell value={value}>{value ?? ''}</EllipsisCell>,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (value) => {
        const formatted = value ? moment(value).format(DAYTIME_FORMAT) : '';
        return <EllipsisCell value={formatted}>{formatted}</EllipsisCell>;
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 280,
      render: (value) => {
        const email = value || '';
        return <EllipsisCell value={email}>{email}</EllipsisCell>;
      },
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      width: 140,
      render: (value) => <EllipsisCell value={value}>{value ?? ''}</EllipsisCell>,
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 160,
      render: (value) => {
        const formatted = value ? moment(value).format(DAYTIME_FORMAT) : '';
        return <EllipsisCell value={formatted}>{formatted}</EllipsisCell>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      fixed: 'right',
      width: 120,
      key: 6,
      render: (_, record) => <StatusCell record={record} onStatusChange={onStatusChange} />,
    },
  ];

  if (withAction) {
    columns.push({
      key: 'action',
      width: 120,
      fixed: 'right',
      render(record) {
        return withAction(record);
      },
    });
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const scrollX = 1100;

  return (
    <div className="table-users table-common table-responsive-wrapper">
      <Table
        columns={columns}
        rowSelection={rowSelection}
        scroll={{ x: scrollX }}
        dataSource={dataSource}
        rowClassName={(record, index) => (index % 2 === 0 ? 'table-row-dark' : 'table-row-light')}
        {...props}
      />
    </div>
  );
};

export default UsersTable;
